import { useEffect, useMemo, useState } from "react";
import { apiSummary, type SummaryResponse } from "../api";
import { ExpensePieChart, sliceFillColor } from "../components/ExpensePieChart";
import { currentMonthISO, formatCents, formatMonthLongYear } from "../utils/money";
import {
  readIncludeOffBudgetFromStorage,
  writeIncludeOffBudgetToStorage,
} from "../includeOffBudgetPreference";

type CategoryBreakdownRow = {
  label: string;
  valueCents: number;
  categoryId: string;
  color: string | null;
};

export function ReportsPage() {
  const [month, setMonth] = useState(currentMonthISO);
  const [includeOffBudget, setIncludeOffBudget] = useState(readIncludeOffBudgetFromStorage);
  const [summary, setSummary] = useState<SummaryResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const s = await apiSummary(month, { excludeOffBudget: !includeOffBudget });
        if (!cancelled) setSummary(s);
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : "Failed to load");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [month, includeOffBudget]);

  useEffect(() => {
    writeIncludeOffBudgetToStorage(includeOffBudget);
  }, [includeOffBudget]);

  const expenseBreakdown = useMemo(() => {
    if (!summary) {
      return {
        slices: [] as { label: string; valueCents: number; color: string | null }[],
        rows: [] as CategoryBreakdownRow[],
        totalCents: 0,
      };
    }
    const rows: CategoryBreakdownRow[] = summary.byCategory
      .filter((c) => c.kind === "expense")
      .map((c) => ({
        label: c.name,
        valueCents: Math.abs(c.totalCents),
        categoryId: c.categoryId,
        color: c.color ?? null,
      }))
      .filter((c) => c.valueCents > 0)
      .sort((a, b) => b.valueCents - a.valueCents);
    const totalCents = rows.reduce((s, r) => s + r.valueCents, 0);
    return {
      slices: rows.map(({ label, valueCents, color }) => ({ label, valueCents, color })),
      rows,
      totalCents,
    };
  }, [summary]);

  const incomeBreakdown = useMemo(() => {
    if (!summary) {
      return {
        slices: [] as { label: string; valueCents: number; color: string | null }[],
        rows: [] as CategoryBreakdownRow[],
        totalCents: 0,
      };
    }
    const rows: CategoryBreakdownRow[] = summary.byCategory
      .filter((c) => c.kind === "income")
      .map((c) => ({
        label: c.name,
        valueCents: Math.abs(c.totalCents),
        categoryId: c.categoryId,
        color: c.color ?? null,
      }))
      .filter((c) => c.valueCents > 0)
      .sort((a, b) => b.valueCents - a.valueCents);
    const totalCents = rows.reduce((s, r) => s + r.valueCents, 0);
    return {
      slices: rows.map(({ label, valueCents, color }) => ({ label, valueCents, color })),
      rows,
      totalCents,
    };
  }, [summary]);

  const expensePieTitle = `Expense breakdown by category for ${formatMonthLongYear(month)}`;
  const incomePieTitle = `Income breakdown by category for ${formatMonthLongYear(month)}`;

  if (loading && !summary) {
    return <p className="muted">Loading report…</p>;
  }

  return (
    <div className="stack">
      {error ? <p className="error">{error}</p> : null}

      <div className="card row-between">
        <div>
          <h2 className="mt-0">Reports</h2>
          <p className="muted mb-0">
            See how income and expenses break down by category in the selected month.
          </p>
        </div>
        <label className="inline-label">
          Month
          <input
            type="month"
            value={month}
            onChange={(e) => setMonth(e.currentTarget.value)}
            className="input-compact"
          />
        </label>
      </div>

      <div className="card">
        <div className="toggle-row">
          <div>
            <div className="small" style={{ fontWeight: 600 }}>
              Include Off Budget accounts
            </div>
            <p className="muted small mb-0">
              When off, Off Budget transactions are left out of this report&apos;s income, expenses, pie charts, and
              category totals. Same setting as Overview. Your data is unchanged.
            </p>
          </div>
          <button
            type="button"
            role="switch"
            aria-checked={includeOffBudget}
            aria-label={includeOffBudget ? "Including Off Budget accounts" : "Excluding Off Budget accounts"}
            className={`toggle-switch${includeOffBudget ? " toggle-switch-on" : ""}`}
            onClick={() => setIncludeOffBudget((v) => !v)}
          >
            <span className="toggle-thumb" aria-hidden />
          </button>
        </div>
      </div>

      {summary ? (
        <>
          <div className="card">
            <h3 className="mt-0">Expense pie chart</h3>
            <p className="muted small mb-0">
              Total expenses in {formatMonthLongYear(month)}:{" "}
              <strong className="mono">{formatCents(summary.totalExpenseCents)}</strong>
              {expenseBreakdown.totalCents > 0 ? (
                <> · each wedge is a category&apos;s share of spending that month.</>
              ) : null}
            </p>

            {expenseBreakdown.totalCents === 0 || expenseBreakdown.slices.length === 0 ? (
              <p className="muted mb-0">No expense transactions this month, or categories are all income-only.</p>
            ) : (
              <div className="report-pie-row">
                <div className="report-pie-chart">
                  <ExpensePieChart slices={expenseBreakdown.slices} title={expensePieTitle} />
                </div>
                <div className="report-pie-legend">
                  <div className="table-scroll">
                  <table className="table table-tight">
                    <thead>
                      <tr>
                        <th>Category</th>
                        <th className="right">Share</th>
                        <th className="right">Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {expenseBreakdown.rows.map((row, i) => {
                        const pct =
                          expenseBreakdown.totalCents > 0
                            ? (row.valueCents / expenseBreakdown.totalCents) * 100
                            : 0;
                        return (
                          <tr key={row.categoryId}>
                            <td>
                              <span
                                className="pie-legend-swatch"
                                style={{ background: sliceFillColor(row.color, i) }}
                              />
                              {row.label}
                            </td>
                            <td className="right mono">{pct.toFixed(1)}%</td>
                            <td className="right mono">{formatCents(-row.valueCents)}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="card">
            <h3 className="mt-0">Income pie chart</h3>
            <p className="muted small mb-0">
              Total income in {formatMonthLongYear(month)}:{" "}
              <strong className="mono">{formatCents(summary.totalIncomeCents)}</strong>
              {incomeBreakdown.totalCents > 0 ? (
                <> · each wedge is a category&apos;s share of income that month.</>
              ) : null}
            </p>

            {incomeBreakdown.totalCents === 0 || incomeBreakdown.slices.length === 0 ? (
              <p className="muted mb-0">No income transactions this month, or categories are all expense-only.</p>
            ) : (
              <div className="report-pie-row">
                <div className="report-pie-chart">
                  <ExpensePieChart
                    slices={incomeBreakdown.slices}
                    title={incomePieTitle}
                    caption="Pie chart — income share by category"
                  />
                </div>
                <div className="report-pie-legend">
                  <div className="table-scroll">
                  <table className="table table-tight">
                    <thead>
                      <tr>
                        <th>Category</th>
                        <th className="right">Share</th>
                        <th className="right">Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {incomeBreakdown.rows.map((row, i) => {
                        const pct =
                          incomeBreakdown.totalCents > 0
                            ? (row.valueCents / incomeBreakdown.totalCents) * 100
                            : 0;
                        return (
                          <tr key={row.categoryId}>
                            <td>
                              <span
                                className="pie-legend-swatch"
                                style={{ background: sliceFillColor(row.color, i) }}
                              />
                              {row.label}
                            </td>
                            <td className="right mono">{pct.toFixed(1)}%</td>
                            <td className="right mono">{formatCents(row.valueCents)}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                  </div>
                </div>
              </div>
            )}
          </div>
        </>
      ) : null}
    </div>
  );
}
